import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TabGroup, TabList, Tab } from '@headlessui/react';
import Notification from 'flow/components/ui-components/Notification';
import { twMerge } from 'tailwind-merge';

interface Props {
    selectedKey?: string | number;
    className?: string;
    tabClassName?: string;
    tabs: TabProps[];
    onClick: (tab: TabProps) => void;
}

export interface TabProps {
    key: string | number;
    label: string | React.JSX.Element;
    badgeCounter?: number;
}

const toIndex = (tabs: TabProps[], key?: string | number) =>
    key != null
        ? Math.max(
              0,
              tabs.findIndex((tab) => tab.key === key),
          )
        : 0;

const Tabs = ({
    selectedKey,
    tabs,
    onClick,
    className,
    tabClassName,
}: Props) => {
    const [selectedIndex, setSelectedIndex] = useState(() =>
        toIndex(tabs, selectedKey),
    );

    useEffect(() => {
        if (selectedKey != null) {
            setSelectedIndex(toIndex(tabs, selectedKey));
        }
    }, [selectedKey, tabs]);

    const handleChange = useCallback(
        (index: number) => {
            setSelectedIndex(index);
            onClick(tabs[index]);
        },
        [onClick, tabs],
    );

    const resolvedTabClassName = useMemo(
        () =>
            twMerge(
                'focus-ring relative flex-1 rounded-sm bg-gray-200 p-2 text-sm/4 text-gray-500 transition-colors duration-200 focus-visible:z-10',
                'data-selected:bg-main data-selected:text-white',
                'not-data-selected:hover:darken-10 not-data-selected:hover:text-gray-600',
                tabClassName,
            ),
        [tabClassName],
    );

    return (
        <TabGroup selectedIndex={selectedIndex} onChange={handleChange}>
            <TabList
                className={twMerge('flex rounded-sm bg-gray-200', className)}
            >
                {tabs.map((tab) => (
                    <Tab key={tab.key} className={resolvedTabClassName}>
                        {tab.label}

                        {tab.badgeCounter ? (
                            <Notification className="me-1">
                                {tab.badgeCounter}
                            </Notification>
                        ) : null}
                    </Tab>
                ))}
            </TabList>
        </TabGroup>
    );
};

export default Tabs;
